import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpotifyPlayerContext = createContext();

export const useSpotifyPlayer = () => {
  const context = useContext(SpotifyPlayerContext);
  if (!context) {
    throw new Error('useSpotifyPlayer must be used within SpotifyPlayerProvider');
  }
  return context;
};

export const SpotifyPlayerProvider = ({ children }) => {
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [accessToken, setAccessToken] = useState(null);
  const playerRef = useRef(null);
  const pendingTokenRef = useRef(null);
  const isInitializingRef = useRef(false); // Prevent multiple initializations
  const expectedPlayingStateRef = useRef(null); // Track expected playing state to ignore stale events

  // Load Spotify Web Playback SDK
  useEffect(() => {
    // Check if SDK is already loaded
    if (window.Spotify) {
      console.log('✅ Spotify SDK already loaded');
      return;
    }

    console.log('🎵 Loading Spotify Web Playback SDK...');
    
    // Define OFFICIAL Spotify callback - MUST CREATE PLAYER INSIDE THIS
    window.onSpotifyWebPlaybackSDKReady = () => {
      console.log('🎉 ✅ Spotify SDK Ready Callback Fired!');
      
      // Check if we have a pending token to initialize with
      if (pendingTokenRef.current) {
        console.log('🔄 Initializing player with pending token...');
        initializePlayerInCallback(pendingTokenRef.current);
        pendingTokenRef.current = null;
      }
    };
    
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    
    script.onload = () => {
      console.log('✅ Spotify SDK script loaded');
    };
    
    script.onerror = (error) => {
      console.error('❌ Failed to load Spotify SDK:', error);
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Check premium status
  const checkPremiumStatus = useCallback(async (token) => {
    try {
      console.log('🔍 Checking Spotify Premium status...');
      const response = await axios.get(`${API}/spotify/user/premium`, {
        params: { access_token: token }
      });
      
      const premium = response.data.is_premium;
      setIsPremium(premium);
      console.log(premium ? '✅ User has Spotify Premium' : '❌ User has Spotify Free');
      
      return premium;
    } catch (error) {
      console.error('❌ Failed to check premium status:', error);
      setIsPremium(false);
      return false;
    }
  }, []);

  // Initialize player INSIDE the SDK ready callback - Following official pattern
  const initializePlayerInCallback = useCallback((token) => {
    // CRITICAL: Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      console.log('⚠️ Player already initializing - skipping duplicate call');
      return;
    }

    // If player already exists and is ready, don't reinitialize
    if (playerRef.current && isReady) {
      console.log('⚠️ Player already exists and ready - skipping reinitialization');
      return;
    }

    isInitializingRef.current = true;
    console.log('🎵 Creating Spotify Player INSIDE onSpotifyWebPlaybackSDKReady callback...');

    // Disconnect existing player if any
    if (playerRef.current) {
      console.log('🔄 Disconnecting existing player...');
      try {
        playerRef.current.disconnect();
      } catch (e) {
        console.warn('Error disconnecting player:', e);
      }
      playerRef.current = null;
    }

    // OFFICIAL PATTERN: Create player with token
    const spotifyPlayer = new window.Spotify.Player({
      name: 'BEATSEEK Jam Session',
      getOAuthToken: cb => { 
        console.log('🔑 Spotify requesting OAuth token...');
        cb(token); 
      },
      volume: 0.5
    });

    // Error handling
    spotifyPlayer.addListener('initialization_error', ({ message }) => {
      console.error('❌ Initialization Error:', message);
      isInitializingRef.current = false;
    });
    
    spotifyPlayer.addListener('authentication_error', ({ message }) => {
      console.error('❌ Authentication Error:', message);
      isInitializingRef.current = false;
    });
    
    spotifyPlayer.addListener('account_error', ({ message }) => {
      console.error('❌ Account Error:', message);
      isInitializingRef.current = false;
    });
    
    spotifyPlayer.addListener('playback_error', ({ message }) => {
      console.error('❌ Playback Error:', message);
    });

    // Ready - THE CRITICAL EVENT
    spotifyPlayer.addListener('ready', ({ device_id }) => {
      console.log('🎉 🎉 🎉 Spotify Player Ready with Device ID:', device_id);
      console.log('🎵 Device ID Type:', typeof device_id);
      console.log('🎵 Device ID Value:', JSON.stringify(device_id));
      
      // ✅ CRITICAL: Activate player immediately to make device visible to Spotify
      console.log('🎯 Activating Spotify Player to register device with Spotify...');
      spotifyPlayer.activateElement();
      console.log('✅ Player activated');
      
      // WORKAROUND: Add delay before setting state to allow Spotify backend to fully register device
      // This is a known issue with Spotify Web Playback SDK - see: https://community.spotify.com/t5/Spotify-for-Developers/Spotify-Web-Playback-SDK-404-device-not-found/td-p/5472057
      setTimeout(() => {
        console.log('✅ Setting Device ID after 2-second delay (Spotify backend registration + activation)');
        setDeviceId(device_id);
        setIsReady(true);
        isInitializingRef.current = false;
      }, 2000); // Increased to 2 seconds to allow activation to complete
    });

    // Not Ready
    spotifyPlayer.addListener('not_ready', ({ device_id }) => {
      console.warn('⚠️ Device ID has gone offline:', device_id);
      setIsReady(false);
      isInitializingRef.current = false;
    });

    // Player state changed
    spotifyPlayer.addListener('player_state_changed', (state) => {
      if (!state) {
        console.log('🎵 Player state: No active playback');
        setCurrentTrack(null);
        setIsPlaying(false);
        return;
      }

      const playerIsPlaying = !state.paused;
      
      console.log('🎵 Player state changed:', {
        paused: state.paused,
        position: state.position,
        track: state.track_window.current_track.name,
        expectedState: expectedPlayingStateRef.current
      });

      // Update track and position always
      setCurrentTrack(state.track_window.current_track);
      setPosition(state.position);
      
      // ✅ Only update if matches expected state OR no expected state set
      if (expectedPlayingStateRef.current === null || expectedPlayingStateRef.current === playerIsPlaying) {
        setIsPlaying(playerIsPlaying);
        expectedPlayingStateRef.current = null; // Clear expected state after match
      } else {
        console.log(`⏭️ Ignoring stale state (expected: ${expectedPlayingStateRef.current}, got: ${playerIsPlaying})`);
      }
    });

    // Connect - Following official pattern
    console.log('🔌 Connecting Spotify Player...');
    spotifyPlayer.connect().then(success => {
      if (success) {
        console.log('✅ Spotify Player connected successfully!');
        console.log('   Waiting for ready event with Device ID...');
      } else {
        console.error('❌ Failed to connect Spotify Player');
        isInitializingRef.current = false;
      }
    });

    setPlayer(spotifyPlayer);
    playerRef.current = spotifyPlayer;
  }, [isReady]);

  // Connect with Spotify token - NEW OFFICIAL PATTERN
  const connect = useCallback(async (token) => {
    console.log('🔌 Connecting to Spotify with OFFICIAL pattern...');
    setAccessToken(token);
    
    // Check premium status first
    const premium = await checkPremiumStatus(token);
    
    if (!premium) {
      console.warn('⚠️ User does not have Spotify Premium - Web Playback SDK unavailable');
      return false;
    }

    // OFFICIAL PATTERN: Check if SDK callback already fired
    if (window.Spotify) {
      console.log('✅ SDK already ready - initializing player immediately');
      initializePlayerInCallback(token);
      return true;
    }

    // OFFICIAL PATTERN: Store token for when callback fires
    console.log('⏳ SDK not ready yet - storing token for callback...');
    pendingTokenRef.current = token;
    
    // Wait a bit for the callback to fire
    const maxAttempts = 100; // 10 seconds
    let attempts = 0;
    
    while (!window.Spotify && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (!window.Spotify) {
      console.error('❌ Spotify SDK failed to load after 10 seconds');
      pendingTokenRef.current = null;
      return false;
    }

    console.log('✅ Spotify SDK loaded - callback should have initialized player');
    return true;
  }, [checkPremiumStatus, initializePlayerInCallback]);

  // Disconnect player
  const disconnect = useCallback(() => {
    if (playerRef.current) {
      console.log('🔌 Disconnecting Spotify Player...');
      playerRef.current.disconnect();
      setPlayer(null);
      setDeviceId(null);
      setIsReady(false);
      setCurrentTrack(null);
      setIsPlaying(false);
    }
  }, []);

  // Play track
  const play = useCallback(async (trackUri, positionMs = 0) => {
    if (!accessToken || !deviceId || !playerRef.current) {
      console.error('❌ Cannot play: No access token, device ID, or player instance');
      console.error('   accessToken:', !!accessToken);
      console.error('   deviceId:', deviceId);
      console.error('   playerRef:', !!playerRef.current);
      return false;
    }

    try {
      // ✅ Set expected playing state - ignore events that don't match
      expectedPlayingStateRef.current = true;
      setIsPlaying(true); // Update immediately
      
      console.log(`🎵 Playing track via Web Playback SDK: ${trackUri} at position ${positionMs}ms`);
      console.log(`   Device ID: ${deviceId}`);
      console.log(`   Token (first 20 chars): ${accessToken.substring(0, 20)}...`);
      
      // DIAGNOSTIC: Check what user info we can get with this token
      console.log('🔍 Checking token permissions...');
      try {
        const userResponse = await axios.get(
          'https://api.spotify.com/v1/me',
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('👤 User profile accessible:', userResponse.data.display_name, '| Product:', userResponse.data.product);
      } catch (err) {
        console.warn('⚠️ Cannot access user profile:', err.response?.status);
      }
      
      // ✅ FIX: Skip device check - Web Playback SDK devices often don't appear in /devices endpoint
      // This is a known Spotify SDK quirk - the device works even if not listed
      // See: https://github.com/spotify/web-playback-sdk/issues/85
      console.log('🎵 Using Web Playback SDK device directly (device_id from SDK)');
      console.log('   Device ID:', deviceId);
      console.log('   Note: Device may not appear in /devices endpoint - this is normal for Web Playback SDK');
      
      // Directly load track into Web Playback SDK player
      const response = await axios.put(
        `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
        {
          uris: [trackUri],
          position_ms: positionMs
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Track loaded into Spotify Player', response.status);
      
      // Expected state will be cleared when player_state_changed fires with matching state
      
      return true;
    } catch (error) {
      // Clear expected state on error
      expectedPlayingStateRef.current = null;
      console.error('❌ Failed to play track:');
      console.error('   Status:', error.response?.status);
      console.error('   Error data:', JSON.stringify(error.response?.data, null, 2));
      console.error('   Error message:', error.message);
      
      // If 401, token might be expired or missing scopes
      if (error.response?.status === 401) {
        console.warn('⚠️ Token authorization failed');
        console.warn('   This usually means:');
        console.warn('   1. Token is expired');
        console.warn('   2. Token is missing required scopes');
        console.warn('   3. Token is not associated with a Premium account');
        
        // Check if it's a scope issue
        if (error.response?.data?.error?.message?.includes('scope')) {
          alert('🔑 Spotify token missing required permissions.\n\nPlease:\n1. Go to Social Hub\n2. Disconnect Spotify\n3. Connect again (this will request updated permissions)\n4. Return to Jam Session');
        } else {
          alert('🔑 Spotify authorization failed.\n\nPlease try:\n1. Click the 🔄 retry button\n2. If that doesn\'t work, reconnect Spotify in Social Hub');
        }
      }
      
      return false;
    }
  }, [accessToken, deviceId]);

  // Pause playback
  const pause = useCallback(async () => {
    if (!accessToken || !deviceId || !playerRef.current) {
      console.error('❌ Cannot pause: No access token, device ID, or player instance');
      return false;
    }

    try {
      // ✅ Set expected playing state - ignore events that don't match
      expectedPlayingStateRef.current = false;
      setIsPlaying(false); // Update immediately
      
      console.log('⏸ Pausing playback via Web Playback SDK');
      
      // Use Spotify Web API to pause Web Playback SDK player
      await axios.put(
        `https://api.spotify.com/v1/me/player/pause?device_id=${deviceId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Playback paused');
      
      // ✅ Clear expected state after delay to allow state change to propagate
      setTimeout(() => {
        expectedPlayingStateRef.current = null;
        console.log('✅ Expected state cleared - listening to player state again');
      }, 2000);
      
      return true;
    } catch (error) {
      // Clear expected state on error too
      expectedPlayingStateRef.current = null;
      console.error('❌ Failed to pause:', error.response?.data || error.message);
      return false;
    }
  }, [accessToken, deviceId]);

  // Seek to position
  const seek = useCallback(async (positionMs) => {
    if (!accessToken || !deviceId || !playerRef.current) {
      console.error('❌ Cannot seek: No access token, device ID, or player instance');
      return false;
    }

    try {
      console.log(`⏩ Seeking to ${positionMs}ms via Web Playback SDK`);
      
      // Use Spotify Web API to seek in Web Playback SDK player
      await axios.put(
        `https://api.spotify.com/v1/me/player/seek?device_id=${deviceId}&position_ms=${positionMs}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Seek successful');
      return true;
    } catch (error) {
      console.error('❌ Failed to seek:', error.response?.data || error.message);
      return false;
    }
  }, [accessToken, deviceId]);

  // Toggle play/pause
  const togglePlayPause = useCallback(async () => {
    if (isPlaying) {
      return await pause();
    } else {
      // Resume by getting current state and playing
      if (playerRef.current) {
        playerRef.current.togglePlay();
        return true;
      }
      return false;
    }
  }, [isPlaying, pause]);

  // Manual retry function for troubleshooting
  const retryConnection = useCallback(async () => {
    console.log('🔄 Manual retry: Disconnecting and reconnecting player...');
    
    // Disconnect existing player
    if (playerRef.current) {
      try {
        playerRef.current.disconnect();
      } catch (e) {
        console.warn('Error disconnecting:', e);
      }
      playerRef.current = null;
      setPlayer(null);
      setDeviceId(null);
      setIsReady(false);
    }
    
    // Reset initialization flag
    isInitializingRef.current = false;
    
    // Wait a moment then reconnect
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (accessToken) {
      console.log('🔄 Reconnecting with existing token...');
      await connect(accessToken);
    } else {
      console.warn('⚠️ No access token available for retry');
    }
  }, [accessToken, connect]);

  const value = {
    player,
    deviceId,
    isPremium,
    isReady,
    currentTrack,
    isPlaying,
    position,
    connect,
    disconnect,
    play,
    pause,
    seek,
    togglePlayPause,
    accessToken,
    checkPremiumStatus,
    retryConnection
  };

  return (
    <SpotifyPlayerContext.Provider value={value}>
      {children}
    </SpotifyPlayerContext.Provider>
  );
};
